import { NavLink } from 'react-router-dom'
import { UserButton, useUser } from '@clerk/react'
import { type ReactNode } from 'react'
import ModelSelector from './ModelSelector'
import useNavStatus, { type PipKind } from '../hooks/useNavStatus'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  selectedModelId: string
  onModelChange: (modelId: string) => void
}

interface NavItem {
  to: string
  code: string // zero-padded sequence number
  label: string
  icon: ReactNode
  key: 'studio' | 'gallery' | 'datasets' | 'settings'
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', code: '01', label: 'STUDIO', key: 'studio', icon: <StudioIcon /> },
  { to: '/gallery', code: '02', label: 'GALLERY', key: 'gallery', icon: <GalleryIcon /> },
  { to: '/datasets', code: '03', label: 'DATASETS', key: 'datasets', icon: <DatasetsIcon /> },
  { to: '/settings', code: '04', label: 'SETTINGS', key: 'settings', icon: <SettingsIcon /> },
]

export default function Sidebar({
  isOpen,
  onClose,
  selectedModelId,
  onModelChange,
}: SidebarProps) {
  const { user } = useUser()
  const baseStatus = useNavStatus()

  // Merge in client-side signals the hook can't see on its own:
  // - low credits → red alert pip on Settings
  const rawCredits = (user?.publicMetadata?.credits as number | undefined) ?? null
  const lowCredits = rawCredits !== null && rawCredits < 50

  const status: Record<NavItem['key'], PipKind> = {
    studio: baseStatus.studio,
    gallery: baseStatus.gallery,
    datasets: baseStatus.datasets,
    settings: lowCredits ? 'alert' : baseStatus.settings,
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-40 h-full w-[var(--sidebar-width)] bg-bg-secondary border-r border-border flex flex-col transition-transform duration-200 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
      >
        {/* Brand header — Playfair wordmark + mono subtitle */}
        <div className="px-5 py-5 border-b border-border">
          <h1 className="brand-wordmark text-xl text-neon neon-text">
            FRANKENVIBES <span className="text-text-primary">LAB</span>
          </h1>
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-text-muted mt-1.5">
            AI · IMAGE · VIDEO STUDIO
          </p>
        </div>

        {/* Numbered nav rail */}
        <nav className="px-2 py-4 space-y-0.5">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-text-muted px-3 pb-2">
            / MODULES
          </p>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all cursor-pointer ${
                  isActive
                    ? 'bg-neon/10 border-l-2 border-neon'
                    : 'border-l-2 border-transparent hover:bg-bg-elevated'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {/* Numbered code — mono, always visible */}
                  <span
                    className={`font-mono text-[10px] font-semibold tracking-wider shrink-0 ${
                      isActive ? 'text-neon' : 'text-text-muted'
                    }`}
                  >
                    {item.code}
                  </span>
                  {/* Icon — accent */}
                  <span
                    className={`shrink-0 transition-colors ${
                      isActive
                        ? 'text-neon'
                        : 'text-text-muted group-hover:text-text-secondary'
                    }`}
                  >
                    {item.icon}
                  </span>
                  {/* Label — mono, uppercase, wide tracking */}
                  <span
                    className={`flex-1 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                      isActive
                        ? 'text-neon'
                        : 'text-text-secondary group-hover:text-text-primary'
                    }`}
                  >
                    {item.label}
                  </span>
                  {/* Status pip */}
                  <StatusPip kind={status[item.key]} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Model selector */}
        <div className="flex-1 overflow-y-auto px-2 py-3 border-t border-border">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-text-muted px-3 pb-2">
            / MODEL
          </p>
          <ModelSelector
            selectedModelId={selectedModelId}
            onChange={onModelChange}
          />
        </div>

        {/* Footer — user button */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-between">
          <UserButton
            appearance={{
              elements: {
                avatarBox: 'w-8 h-8',
              },
            }}
          />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-text-muted">
            ⌘K
          </span>
        </div>
      </aside>
    </>
  )
}

function StatusPip({ kind }: { kind: PipKind }) {
  if (!kind) return <span className="w-2 h-2" />
  const color =
    kind === 'live'
      ? 'bg-neon shadow-[0_0_8px_var(--color-neon-glow)]'
      : kind === 'pending'
      ? 'bg-warning'
      : 'bg-error'
  return (
    <span
      className={`w-2 h-2 rounded-full shrink-0 ${color} ${
        kind !== 'live' ? 'animate-pulse' : ''
      }`}
      aria-label={`${kind} status`}
    />
  )
}

function StudioIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z" />
    </svg>
  )
}

function GalleryIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  )
}

function DatasetsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}
